import React, { useEffect, useState } from 'react'
import { createUseStyles } from 'react-jss'
import theme from '../style/theme'

const YotpoReviews = ({ product }) => {
  const classes = useStyles()
  const [productUrl, setProductUrl] = useState('')

  // Function to move bottom-line element to yotpo-head and filters to header container
  const moveBottomLineToHead = () => {
    try {
      const bottomLineElement = document.querySelector('.yotpo-bottom-line')
      const headElement = document.querySelector('.yotpo-head')
      const headerContainer = document.querySelector('.yotpo-header-container')
      const filtersContainer = document.querySelector('.yotpo-filters-container')
      if (bottomLineElement && headElement) {
        // Remove from current position and append to head
        headElement.appendChild(bottomLineElement)
        // console.log('Successfully moved bottom-line to head')
      }
      // Move filters container to header container
      if (filtersContainer && headerContainer) {
        // Check if filters are not already in header container
        if (!headerContainer.contains(filtersContainer)) {
          headerContainer.appendChild(filtersContainer)
          // console.log('Successfully moved filters to header container')
        }
      }
      // Move star ratings to review left panels
      const starRatings = document.querySelectorAll('.yotpo-star-rating.yotpo-review-star-rating')
      starRatings.forEach((starRating, index) => {
        const reviewLeftPanel = starRating.closest('.yotpo-review')?.querySelector('.yotpo-review-left-panel')
        if (reviewLeftPanel && !reviewLeftPanel.contains(starRating)) {
          // Insert at the beginning of the left panel
          reviewLeftPanel.insertBefore(starRating, reviewLeftPanel.firstChild)
          // console.log(`Successfully moved star rating ${index + 1} to left panel`)
        }
      })

      // Add class to parent of yotpo-header-container if it doesn't have one
      if (headerContainer && headerContainer.parentElement) {
        const parent = headerContainer.parentElement
        if (!parent.className || parent.className.trim() === '') {
          parent.classList.add('yotpo-main-container')
          // console.log('Added yotpo-main-container class to parent')
        }
      }

      if (!bottomLineElement || !headElement || !filtersContainer) {
        // console.log('Elements not found yet, retrying...')
        // Retry after a short delay if elements aren't ready
        setTimeout(moveBottomLineToHead, 500)
      }
    } catch (error) {
      console.error('Error moving elements:', error)
    }
  }

  useEffect(() => {
    // Only run on client side (browser)
    if (typeof window === 'undefined') {
      return
    }

    // Set product URL on client side
    setProductUrl(`${window.location.origin}/products/${product.store?.slug || product.slug || ''}`)

    // Load Yotpo script if not already loaded
    const existingScript = document.querySelector('script[src*="yotpo.com"]')

    if (!existingScript) {
      const script = document.createElement('script')
      script.src = 'https://cdn-widgetsrepository.yotpo.com/v1/loader/7fDdd9CaVCAbneFb2VeCLj4X2tNsushk3G3zPNsE'
      script.async = true
      document.head.appendChild(script)
    }

    // Refresh Yotpo widgets after component mounts or product changes
    if (window.yotpo && window.yotpo.refreshWidgets) {
      setTimeout(() => {
        window.yotpo.refreshWidgets()
        // Move bottom-line element after widgets refresh
        setTimeout(() => {
          moveBottomLineToHead()
        }, 500)
      }, 100)
    } else {
      // If Yotpo is not loaded yet, wait and try to move elements
      setTimeout(() => {
        moveBottomLineToHead()
      }, 1000)
    }
  }, [product])

  // Don't render if no product data
  if (!product || !product.store) {
    return null
  }

  // Extract product data for Yotpo
  const productId = product.store.id
  const productTitle = product.store.title || product.title
  const productImage = product.store.previewImageUrl || product.store.images?.[0]?.url
  const productPrice = product.store.priceRange?.minVariantPrice || 0
  const productDescription = product.store.descriptionHtml || product.store.description || ''

  // Clean up description for Yotpo (remove HTML tags)
  const cleanDescription = productDescription.replace(/<[^>]*>/g, '').trim()

  return (
    <div className={classes.reviewsContainer}>
      <div
        className='yotpo-widget-instance'
        data-yotpo-instance-id='1155729'
        data-yotpo-product-id={productId}
        data-yotpo-name={productTitle}
        data-yotpo-url={productUrl}
        data-yotpo-image-url={productImage}
        data-yotpo-price={productPrice}
        data-yotpo-currency='USD'
        data-yotpo-description={cleanDescription}
      />
    </div>
  )
}

const useStyles = createUseStyles({
  reviewsContainer: {
    width: '100%',
    padding: '110px 0',
    marginTop: '2rem',
    // Override Yotpo's default styles
    '& .yotpo-base-layout': {
      width: '92% !important'
    },
    '& .yotpo-search-filter': {
      borderRadius: '0 !important'
    },
    '& #yotpo-main-widget-btn[data-v-4f716a87]': {
      border: `1px solid ${theme.colors.primary} !important`,
      borderRadius: '0 !important',
      backgroundColor: 'transparent !important',
      color: `${theme.colors.primary} !important`,
      fontWeight: '400 !important'
    },
    // Create 2-column layout
    '& .yotpo-main-layout': {
      // display: 'flex !important',
      flexDirection: 'row !important',
      gap: '2rem !important',
      justifyContent: 'space-between !important',
      alignItems: 'flex-start !important'
    },
    '& .yotpo-main-container': {
      width: '50% !important'
    },
    '& .yotpo-header-container': {
      display: 'flex !important',
      flexDirection: 'row-reverse !important'
    },
    '& .yotpo-header-container[data-v-012a3912]': {
      display: 'flex !important',
      flexDirection: 'row-reverse !important'
    },
    // Left column: Title and rating summary
    '& .yotpo-head': {
      gridColumn: '1 / 2 !important',
      marginBottom: '1rem !important',
      display: 'flex !important',
      flexDirection: 'column !important'
    },
    '& .yotpo-display-l .yotpo-main-layout .yotpo-head': {
      marginTop: '0 !important'
    },
    '& .yotpo-display-m .yotpo-main-layout .yotpo-head': {
      marginTop: '0 !important'
    },
    '& .yotpo-head .yotpo-headline': {
      alignSelf: 'flex-start !important',
      fontSize: '48px !important',
      paddingBottom: '20px',
      color: `${theme.colors.primary} !important`,
      fontFamily: `${theme.fonts.headings} !important`
    },
    '& .yotpo-layout-header-wrapper': {
      gridColumn: '1 / 2 !important',
      display: 'flex !important',
      flexDirection: 'column !important',
      marginBottom: '24px !important'
    },
    '& .yotpo-bottom-line': {
      width: '100% !important',
      marginBottom: '1rem !important',
      flexDirection: 'column !important'
    },
    '& .yotpo-vertical-border': {
      display: 'none !important'
    },
    '& .yotpo-star-distribution': {
      border: 'none !important',
      paddingBottom: '0 !important'
    },
    '& .yotpo-bottom-line .yotpo-star-distribution': {
      paddingInlineEnd: '0 !important',
      paddingLeft: '0 !important',
      paddingTop: '20px !important',
      marginTop: '25px !important'
    },
    '& .yotpo-bottom-line .yotpo-bottom-line-left-panel.yotpo-bottom-line-score': {
      fontSize: '14px !important',
      lineHeight: '20px !important',
      color: `${theme.colors.primary} !important`,
      paddingTop: '2px !important',
      position: 'relative !important'
    },
    '& .yotpo-bottom-line .yotpo-bottom-line-right-panel': {
      paddingTop: '0 !important'
    },
    '& .yotpo-bottom-line-basic-text': {
      paddingBottom: '20px !important',
      fontSize: '14px !important',
      color: `${theme.colors.primary} !important`,
      fontFamily: `${theme.fonts.body} !important`
    },
    '& .yotpo-bottom-line-text': {
      position: 'absolute !important',
      left: '0 !important'
    },
    '& .yotpo-bottom-line .yotpo-summary-section': {
      position: 'relative !important',
      [theme.breakpoints.down('md')]: {
        alignItems: 'self-start !important'

      }
    },
    // Override star rating colors
    '& .yotpo-star-rating-icon path': {
      stroke: `${theme.colors.primary} !important`,
      fill: `${theme.colors.primary} !important`
    },
    // Add "Stars" text after star distribution numbers
    '& .yotpo-star-distribution-index-wrapper': {
      width: '42px !important',
      display: 'inline-flex !important',
      alignItems: 'center !important'
    },
    '& .yotpo-star-distribution-index-wrapper .yotpo-star-distribution-index': {
      color: `${theme.colors.primary} !important`,
      '&::after': {
        content: '" Stars" !important',
        marginLeft: '4px !important',
        fontSize: 'inherit !important',
        color: 'inherit !important',
        whiteSpace: 'nowrap !important'
      }
    },
    '& .yotpo-star-distribution-index-wrapper .yotpo-star-rating-icon': {
      display: 'none !important'
    },
    '& .yotpo-star-distribution-bar': {
      marginRight: '0px !important'
    },
    '& .yotpo-star-distribution-value': {
      color: `${theme.colors.primary} !important`
    },
    '& .yotpo-progress-bar .yotpo-progress-bar-score': {
      background: `${theme.colors.primary} !important`
    },
    // Style read more link
    '& .yotpo-text-container .yotpo-read-more': {
      color: `${theme.colors.primary} !important`
    },
    '& .yotpo-read-more-text': {
      fontFamily: `${theme.fonts.body} !important`,
      color: `${theme.colors.primary} !important`,
      fontSize: '14px !important'
    },
    // Style date format
    '& .yotpo-date-format': {
      color: `${theme.colors.primary} !important`,
      opacity: '0.5 !important',
      lineHeight: '18px !important',
      fontFamily: `${theme.fonts.body} !important`,
      fontSize: '13px !important'
    },
    // Individual review cards - merge left and center columns
    '& .yotpo-review': {
      display: 'grid !important',
      gridTemplateColumns: '1fr auto !important',
      gap: '1rem !important',
      alignItems: 'flex-start !important'
    },
    // Style review display for large and medium screens
    '& .yotpo-display-l .yotpo-review:not(.yotpo-media-modal-review), & .yotpo-display-m .yotpo-review:not(.yotpo-media-modal-review)': {
      margin: '20px 0px 40px 0 !important'
    },
    '& .yotpo-review-left-panel': {
      gridColumn: '1 / 2 !important',
      display: 'flex !important',
      flexDirection: 'column !important',
      gap: '0.5rem !important'
    },
    '& .yotpo-review-center-panel': {
      gridColumn: '1 / 2 !important',
      marginTop: '0.5rem !important',
      paddingLeft: '0 !important',
      paddingRight: '0 !important'
    },
    '& .yotpo-review-right-panel': {
      gridColumn: '2 / 3 !important'
    },
    // Style review titles
    '& .yotpo-display-l .yotpo-review:not(.yotpo-media-modal-review) .yotpo-review-center-panel .yotpo-review-rating-title .yotpo-review-title': {
      order: '1 !important',
      marginLeft: '0 !important',
      textAlign: 'start !important',
      color: `${theme.colors.primary} !important`,
      fontWeight: '600 !important',
      fontFamily: `${theme.fonts.headings} !important`,
      flex: 'unset !important'
    },
    '& .yotpo-display-m .yotpo-review:not(.yotpo-media-modal-review) .yotpo-review-center-panel .yotpo-review-rating-title .yotpo-review-title': {
      order: '1 !important',
      marginLeft: '0 !important',
      textAlign: 'start !important',
      color: `${theme.colors.primary} !important`,
      fontWeight: '600 !important',
      fontFamily: `${theme.fonts.headings} !important`,
      flex: 'unset !important'
    },
    // Right column: Filters and reviews
    '& .yotpo-filters-container': {
      gridColumn: '2 / 3 !important',
      marginTop: '0 !important',
      // Remove when moved to header container
      '&:not(.yotpo-header-container .yotpo-filters-container)': {
        display: 'none !important'
      }
    },
    '& .yotpo-filters-container .yotpo-filters-container-inner .yotpo-filters-top-panel': {
      display: 'none !important'
    },
    '& .yotpo-filters-container .yotpo-filters-container-inner': {
      display: 'block !important',
      flexDirection: 'unset !important',
      width: 'auto !important',
      alignItems: 'unset !important',
      rowGap: 'unset !important',
      columnGap: 'unset !important',
      MozColumnGap: 'unset !important'
    },
    '& .yotpo-review-border-smooth:first-child': {
      display: 'none !important'
    },
    '& .yotpo-review-border-smooth': {
      height: '1px !important'
    },
    '& .yotpo-reviews-widget-dropdown': {
      border: `1px solid ${theme.colors.primary} !important`,
      color: `${theme.colors.primary} !important`,
      fontSize: '14px !important',
      padding: '0.5rem 1rem !important',
      borderRadius: '0 !important'
    },
    '& #yotpo-reviews-container': {
      gridColumn: '2 / 3 !important',
      marginTop: '1rem !important'
    },
    // Responsive design
    '@media (max-width: 768px)': {
      padding: '0 !important',
      marginTop: '0 !important',
      '& .yotpo-main-layout': {
        display: 'flex !important',
        flexDirection: 'column !important'
      },
      '& .yotpo-main-container': {
        width: '100% !important'
      },
      '& .yotpo-head': {
        gridColumn: '1 / 2 !important'
      },
      '& .yotpo-head .yotpo-headline': {
        fontSize: '30px !important'
      },
      '& .yotpo-display-s .yotpo-head .yotpo-headline': {
        padding: '0 !important'
      },
      '& .yotpo-layout-header-wrapper': {
        gridColumn: '1 / 2 !important'
      },
      '& .yotpo-filters-container': {
        gridColumn: '1 / 2 !important'
      },
      '& .yotpo-display-s .yotpo-filters-container': {
        display: 'flex !important',
        margin: '0 !important',
        width: '100% !important',
        placeContent: 'flex-start !important',
        flexDirection: 'row !important'
      },
      '& .yotpo-reviews-widget-dropdown': {
        maxWidth: '147px !important'
      },
      '& #yotpo-main-widget-btn': {
        padding: '0 !important'
      },
      '& #yotpo-reviews-container': {
        gridColumn: '1 / 2 !important'
      },
      // Mobile specific review styling
      '& .yotpo-display-s .yotpo-review, & .yotpo-media-modal .yotpo-review': {
        margin: '20px 0 !important'
      },
      // Mobile specific review title styling
      '& .yotpo-display-s .yotpo-review .yotpo-review-center-panel .yotpo-review-title, & .yotpo-media-modal .yotpo-review .yotpo-review-center-panel .yotpo-review-title': {
        marginTop: '0 !important',
        marginBottom: '0 !important'
      },
      // Mobile specific review content styling
      '& .yotpo-display-s .yotpo-review .yotpo-review-center-panel .yotpo-review-content, & .yotpo-media-modal .yotpo-review .yotpo-review-center-panel .yotpo-review-content': {
        marginTop: '10px !important'
      },
      // Mobile specific review gap styling
      '& .yotpo-review': {
        gap: '0rem !important'
      },
      // Mobile specific reviewer styling
      '& .yotpo-reviewer': {
        marginTop: '2px !important',
        marginBottom: '5px !important'
      }
    }
  },
  reviewsHeader: {
    marginBottom: '2rem',
    textAlign: 'center'
  },
  reviewsTitle: {
    fontSize: '2rem',
    fontFamily: theme.fonts.headings,
    fontWeight: theme.fonts.headingsFontWeight,
    color: theme.colors.primary,
    margin: 0
  }
}, { name: 'YotpoReviews' })

export default YotpoReviews
